import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import clsx from "clsx";
import { Product } from "../../interfaces/product.interface";
import { useTranslation } from "react-i18next";

interface ServiceProductCardProps {
  product: Product;
  index?: number;
}

const ServiceProductCard: React.FC<ServiceProductCardProps> = ({
  product,
  index = 0,
}) => {
  const { t } = useTranslation("products");
  const featured = product.isFeatured;

  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08, duration: 0.4 }}
      className={clsx(
        "relative flex flex-col h-full rounded-2xl overflow-hidden",
        "bg-white/5 backdrop-blur-md border",
        featured
          ? "border-neon-green border-2 glow-green shadow-neon-green-lg"
          : "border-white/10 hover:border-neon-green/40"
      )}
    >
      {featured && (
        <>
          <div className="absolute inset-0 rounded-2xl pointer-events-none animate-pulse opacity-30 bg-gradient-to-br from-neon-green/20 via-transparent to-neon-blue/20" />
          {product.featuredLabel && (
            <span className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full text-caption font-bold bg-neon-green text-space shadow-neon-green">
              🔥 {product.featuredLabel}
            </span>
          )}
        </>
      )}

      <Link to={`/products/${product.id}`} className="block shrink-0">
        <div className="relative aspect-[16/10] overflow-hidden bg-space-light">
          <img
            src={product.imageUrl}
            alt={product.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-space/80 via-transparent to-transparent" />
        </div>
      </Link>

      <div className="relative flex flex-col flex-1 p-5 md:p-6">
        <p className="text-caption text-neon-blue mb-2">{product.category}</p>

        <h3 className="text-h4 text-white mb-3 leading-snug min-h-[3rem]">
          <Link
            to={`/products/${product.id}`}
            className="hover:text-neon-green transition-colors"
          >
            {product.name}
          </Link>
        </h3>

        <p className="text-body-sm text-gray-400 leading-relaxed mb-4 flex-1">
          {product.description}
        </p>

        {product.highlights && (
          <ul className="flex flex-wrap gap-2 mb-4">
            {product.highlights.map((item) => (
              <li
                key={item}
                className="text-caption px-2 py-1 rounded-md bg-white/5 text-gray-300 border border-white/10"
              >
                {item}
              </li>
            ))}
          </ul>
        )}

        {product.savingsLabel && (
          <p className="text-body-sm text-neon-green font-semibold mb-4">
            {product.savingsLabel}
          </p>
        )}

        <div className="mt-auto pt-4 border-t border-white/10 space-y-4">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span
              className={clsx(
                "font-extrabold tabular-nums",
                featured ? "text-3xl text-neon-green" : "text-2xl text-white"
              )}
            >
              HKD {product.price.toLocaleString()}
            </span>
            {product.originalPrice && (
              <span className="text-body-sm text-gray-500 line-through">
                HKD {product.originalPrice.toLocaleString()}
              </span>
            )}
          </div>

          <Link
            to={`/products/${product.id}`}
            className={clsx(
              "block w-full text-center rounded-full py-3 px-4 text-body-sm font-semibold transition-all",
              featured
                ? "bg-gradient-brand text-space hover:opacity-90 shadow-neon-green animate-breathe"
                : "bg-white/10 text-white border border-white/20 hover:bg-neon-green/20 hover:border-neon-green/50 hover:text-neon-green"
            )}
          >
            {t("view_details")}
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default ServiceProductCard;
